/**
 * Factions and their relationships.
 *
 * The IDs stay the colours they were drafted as, because they end up in the
 * database, in save data and in asset filenames — renaming them would be a
 * migration for no gain. `name` is what a player ever sees, and nothing should
 * be showing "yellow" where "The Deep Cheese Front" belongs.
 */
export const FACTION_IDS = ['red', 'blue', 'green', 'yellow', 'turquoise', 'rogue'];
/** Factions a player can actually align with. Rogues are not joinable. */
export const ALIGNABLE_FACTION_IDS = FACTION_IDS.filter((id) => id !== 'rogue');
export const FACTIONS = {
    red: {
        id: 'red',
        name: 'Cripzon Defence Syndicate',
        abbreviation: 'CDS',
        leader: 'Admiral Cripzon James',
        description: 'Military and defence. Turns up to help against rogues, and much more readily if you fly with them.',
        allies: ['blue'],
        dislikes: ['green'],
        alwaysNeutral: false,
        alwaysHostile: false,
    },
    blue: {
        id: 'blue',
        name: 'The Meridian Order',
        abbreviation: 'MIO',
        leader: 'Captain Fleeg',
        description: 'Heads construction across the galaxy. Marginally more likely to hold an advanced fabricator.',
        allies: ['red'],
        dislikes: ['yellow'],
        alwaysNeutral: false,
        alwaysHostile: false,
    },
    green: {
        id: 'green',
        name: 'The Gloamly Space Guild',
        abbreviation: 'GSG',
        leader: 'Grandmaster Gloamly',
        description: 'Trade and commerce, and expert resource gatherers.',
        allies: ['yellow'],
        dislikes: ['blue'],
        alwaysNeutral: false,
        alwaysHostile: false,
    },
    yellow: {
        id: 'yellow',
        name: 'The Deep Cheese Front',
        abbreviation: null,
        leader: 'Baron Von Cheddar',
        description: 'Seeking a moon or planet made entirely of cheese. Deadly serious about it.',
        allies: ['green'],
        dislikes: ['red'],
        alwaysNeutral: false,
        alwaysHostile: false,
    },
    turquoise: {
        id: 'turquoise',
        name: 'The United Galactic Federation',
        abbreviation: 'UGF',
        leader: 'President Gort',
        description: 'Neutral ground for everyone. No discounts — the benefit is that its doors never close to you.',
        allies: [],
        dislikes: [],
        // Offers no discounts; the benefit is guaranteed access no matter how badly
        // the player has burned every other faction.
        alwaysNeutral: true,
        alwaysHostile: false,
    },
    rogue: {
        id: 'rogue',
        name: 'Rogues',
        abbreviation: null,
        leader: null,
        description: 'Not a faction. Hostile to everyone including each other, found anywhere, and holding nothing.',
        allies: [],
        dislikes: [],
        alwaysNeutral: false,
        alwaysHostile: true,
    },
};
export function getFaction(id) {
    return FACTIONS[id];
}
/** Mutual by construction — see the Faction.allies note. */
export function areAllied(a, b) {
    if (a === b)
        return true;
    return FACTIONS[a].allies.includes(b);
}
/** Directional: `dislikes(a, b)` asks whether A dislikes B, not the reverse. */
export function dislikes(a, b) {
    if (a === b)
        return false;
    if (FACTIONS[a].alwaysNeutral || FACTIONS[b].alwaysNeutral)
        return false;
    if (FACTIONS[a].alwaysHostile || FACTIONS[b].alwaysHostile)
        return true;
    return FACTIONS[a].dislikes.includes(b);
}
/** True only when the antipathy runs both ways. */
export function mutuallyHostile(a, b) {
    return dislikes(a, b) && dislikes(b, a);
}
// ─── Standing and station hostility ──────────────────────────────────────────
/** Reputation runs 0..MAX_STANDING per faction. */
export const MAX_STANDING = 100;
/**
 * Whether a station's turrets open fire on a player.
 *
 * Players can dock anywhere by default. The single exception: being at *max*
 * standing with a faction this station's owner dislikes. Partial standing is
 * never enough — you have to have fully committed to the other side.
 *
 * Station allegiance is fixed by the world seed and never changes, so this is a
 * pure function of (seed-derived owner, player standing) with nothing stored.
 */
export function stationIsHostile(allegiance, standing) {
    if (allegiance === null)
        return false;
    const owner = FACTIONS[allegiance];
    if (owner.alwaysNeutral)
        return false;
    return owner.dislikes.some((enemy) => (standing[enemy] ?? 0) >= MAX_STANDING);
}
//# sourceMappingURL=factions.js.map